import axios from 'axios';
import type {
  Job,
  Question,
  Candidate,
  CandidateDetail,
  CandidatesResponse,
  SendInvitationResponse,
  CreateJobRequest,
  UpdateJobRequest,
  CreateQuestionRequest,
  UpdateQuestionRequest,
  CreateCandidateRequest,
} from '../types';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://shandong-interview-api.fly.dev/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to extract error message from unknown error type
export const getErrorMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { detail?: string } } }).response;
    return response?.data?.detail || null;
  }
  if (err && typeof err === 'object' && 'message' in err) {
    return (err as { message: string }).message;
  }
  return null;
};

// Jobs API
export const jobsAPI = {
  getAll: () => api.get<Job[]>('/jobs').then((res) => res.data),

  getById: (id: number) => api.get<Job>(`/jobs/${id}`).then((res) => res.data),

  create: (data: CreateJobRequest) =>
    api.post<Job>('/jobs', data).then((res) => res.data),

  update: (id: number, data: UpdateJobRequest) =>
    api.put<Job>(`/jobs/${id}`, data).then((res) => res.data),

  delete: (id: number) => api.delete(`/jobs/${id}`).then((res) => res.data),

  close: (id: number) => api.post(`/jobs/${id}/close`).then((res) => res.data),
};

// Questions API
export const questionsAPI = {
  getAll: (jobId: number) =>
    api.get<Question[]>(`/jobs/${jobId}/questions`).then((res) => res.data),

  create: (jobId: number, data: CreateQuestionRequest) =>
    api
      .post<Question>(`/jobs/${jobId}/questions`, data)
      .then((res) => res.data),

  update: (jobId: number, questionId: number, data: UpdateQuestionRequest) =>
    api
      .put<Question>(`/jobs/${jobId}/questions/${questionId}`, data)
      .then((res) => res.data),

  delete: (jobId: number, questionId: number) =>
    api
      .delete(`/jobs/${jobId}/questions/${questionId}`)
      .then((res) => res.data),
};

// Candidates API
export const candidatesAPI = {
  getAll: (jobId: number) =>
    api
      .get<CandidatesResponse>(`/jobs/${jobId}/candidates`)
      .then((res) => res.data),

  getById: (jobId: number, candidateId: number) =>
    api
      .get<CandidateDetail>(`/jobs/${jobId}/candidates/${candidateId}`)
      .then((res) => res.data),

  create: (jobId: number, data: CreateCandidateRequest) =>
    api
      .post<Candidate>(`/jobs/${jobId}/candidates`, data)
      .then((res) => res.data),

  delete: (jobId: number, candidateId: number) =>
    api
      .delete(`/jobs/${jobId}/candidates/${candidateId}`)
      .then((res) => res.data),

  sendInvitation: (jobId: number, candidateId: number) =>
    api
      .post<SendInvitationResponse>(
        `/jobs/${jobId}/candidates/${candidateId}/send-invitation`
      )
      .then((res) => res.data),
};
